// The Buttons block's config, read the same way by both templates and
// the editor. No imports, so the harness can run it in node.
//
// A button is one of three shapes:
//   icon    the icon alone in a square or circle (the title becomes
//           its label for screen readers)
//   button  an icon and a title in a row, with an optional second line
//   card    a box: icon on top, a title, a few lines of text
// Every colour is the button's own; an empty one falls back to the
// site's accent (background) and white (text and icon). Any of the
// three can carry a background photo instead, darkened so the words
// on it read; the background colour shows only while it loads.

export const BUTTON_KINDS = [
    { key: 'icon', label: 'Icon only' },
    { key: 'button', label: 'Button' },
    { key: 'card', label: 'Box' },
];
export const BUTTON_SHAPES = [
    { key: 'rounded', label: 'Rounded' },
    { key: 'pill', label: 'Pill / circle' },
    { key: 'square', label: 'Square corners' },
];
export const BUTTON_SIZES = [
    { key: 's', label: 'Small' },
    { key: 'm', label: 'Medium' },
    { key: 'l', label: 'Large' },
];

// Where the icon and words sit inside the button. A Button is a row
// (icon beside the words), so vertical is where the icon sits against
// two lines of words; a Box is a column, so vertical is where its
// contents sit when the box is taller than they are. Icon only is
// always centered.
export const BUTTON_H_ALIGNS = [
    { key: 'left', label: 'Left' },
    { key: 'center', label: 'Center' },
    { key: 'right', label: 'Right' },
];
export const BUTTON_V_ALIGNS = [
    { key: 'top', label: 'Top' },
    { key: 'center', label: 'Middle' },
    { key: 'bottom', label: 'Bottom' },
];
// Icon size in px; empty means the size that goes with the button's
// own size.
export const ICON_SIZE_MIN = 12;
export const ICON_SIZE_MAX = 96;

export function iconSize(v) {
    const n = Math.round(Number(v));
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.max(ICON_SIZE_MIN, Math.min(ICON_SIZE_MAX, n));
}

export const BUTTON_DEFAULTS = [
    { kind: 'button', icon: 'site:heart', title: 'Donate', text: '', href: '/get-involved#donate', new_tab: false, shape: 'rounded', size: 'm', bg: '', fg: '', icon_color: '', border: '' },
    { kind: 'button', icon: 'site:event', title: 'Upcoming events', text: '', href: '/events', new_tab: false, shape: 'rounded', size: 'm', bg: '#ffffff', fg: '#1b1d1a', icon_color: '', border: '#1b1d1a' },
];

const KIND_KEYS = BUTTON_KINDS.map((k) => k.key);
const SHAPE_KEYS = BUTTON_SHAPES.map((k) => k.key);
const SIZE_KEYS = BUTTON_SIZES.map((k) => k.key);
const H_KEYS = BUTTON_H_ALIGNS.map((k) => k.key);
const V_KEYS = BUTTON_V_ALIGNS.map((k) => k.key);

// Only a colour the picker can produce: #rgb, #rrggbb or #rrggbbaa.
// The value lands in a style attribute, so anything else is dropped
// rather than passed through.
export function safeColor(v) {
    const s = String(v || '').trim();
    return /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.test(s) ? s : '';
}

// A row with neither an icon nor a title is a half-added button in
// the editor; drawn, it is an empty coloured box that does nothing.
// An icon-only button with no icon is the same thing.
export function buttonItems(config) {
    const rows = Array.isArray(config?.items) ? config.items : [];
    return rows
        .filter(Boolean)
        .map((r) => ({
            kind: KIND_KEYS.includes(r.kind) ? r.kind : 'button',
            icon: String(r.icon || '').trim(),
            title: String(r.title || '').trim(),
            text: String(r.text || '').trim(),
            href: String(r.href || '').trim(),
            new_tab: Boolean(r.new_tab),
            shape: SHAPE_KEYS.includes(r.shape) ? r.shape : 'rounded',
            size: SIZE_KEYS.includes(r.size) ? r.size : 'm',
            bg: safeColor(r.bg),
            fg: safeColor(r.fg),
            icon_color: safeColor(r.icon_color),
            border: safeColor(r.border),
            h_align: H_KEYS.includes(r.h_align) ? r.h_align : 'left',
            v_align: V_KEYS.includes(r.v_align) ? r.v_align : (r.kind === 'card' ? 'top' : 'center'),
            icon_size: iconSize(r.icon_size),
            image: safeImage(r.image),
            focus: { x: pct(r.focus?.x, 50, 100), y: pct(r.focus?.y, 50, 100) },
            overlay: pct(r.overlay, IMAGE_SHADE_DEFAULT, IMAGE_SHADE_MAX),
        }))
        .filter((b) => (b.kind === 'icon' ? b.icon !== '' : (b.icon !== '' || b.title !== '')));
}

// A background photo: a path on this site or a web address (an upload
// comes back as an absolute url on APP_URL's host), and
// nothing that could close the url() it lands in.
export function safeImage(v) {
    const s = String(v || '').trim();
    if (!/^(\/[^/]|https?:\/\/)/.test(s)) return '';
    return /["'()\\\s<>]/.test(s) ? '' : s;
}

// The point of the photo that stays in view, and how much it is
// darkened so the words read. Same meaning as a hero photo's
// (heroImage.js), repeated here so this file keeps no imports.
export const IMAGE_SHADE_DEFAULT = 35;
export const IMAGE_SHADE_MAX = 80;
function pct(v, fallback, max) {
    const n = Number(v);
    if (v === null || v === undefined || v === '' || !Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(0, Math.round(n)));
}

export function buttonsAlign(config) {
    return ['left', 'center', 'right'].includes(config?.align) ? config.align : 'left';
}

// The inline colours for one button.
export function buttonStyle(b) {
    const v = {};
    if (b.bg) v['--sb-bg'] = b.bg;
    if (b.fg) v['--sb-fg'] = b.fg;
    if (b.icon_color) v['--sb-icon'] = b.icon_color;
    if (b.border) v['--sb-border'] = b.border;
    if (b.icon_size) v['--sb-icon-size'] = `${b.icon_size}px`;
    if (b.image) {
        v['--sb-image'] = `url("${b.image}")`;
        v['--sb-pos'] = `${b.focus?.x ?? 50}% ${b.focus?.y ?? 50}%`;
        v['--sb-shade'] = String((b.overlay ?? IMAGE_SHADE_DEFAULT) / 100);
    }
    return v;
}
